import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { IPlanDialogState } from '@foxera/shared/interfaces/workout-plan-dialog.interface';

@Injectable({
  providedIn: 'root'
})
export class PlanDialogService {
  public isVisible = signal<boolean>(false);
  public clientId = signal<number>(0);
  public planId = signal<number | undefined>(undefined);
  public activeJobId = signal<string | undefined>(undefined);
  public trainingType = signal<string | undefined>(undefined);
  public branchId = signal<string | undefined>(undefined);
  public isLiveWorkoutSessionVisible = signal<boolean>(false);

  // Observable for plan saved events
  private planSavedSubject = new Subject<void>();
  public planSaved$ = this.planSavedSubject.asObservable();

  /**
   * Open the plan dialog with a specific plan ID
   */
  openPlanDialog(clientId: number, planId?: number): void {
    this.isVisible.set(true);
    this.clientId.set(clientId);
    this.planId.set(planId);
    this.activeJobId.set(undefined);
    this.trainingType.set(undefined);
    this.branchId.set(undefined);
  }

  /**
   * Open the plan dialog for generating a new plan (no planId or activeJobId)
   * @param clientId Client ID
   * @param trainingType Training type (Standard or Athlete) - required
   */
  openPlanDialogForGeneration(clientId: number, trainingType: string, branchId: string): void {
    this.isVisible.set(true);
    this.clientId.set(clientId);
    this.planId.set(undefined);
    this.activeJobId.set(undefined);
    this.trainingType.set(trainingType);
    this.branchId.set(branchId);
  }

  /**
   * Open the plan dialog reconnecting to an active job
   */
  openPlanDialogWithJob(clientId: number, activeJobId: string): void {
    this.isVisible.set(true);
    this.clientId.set(clientId);
    this.planId.set(undefined);
    this.activeJobId.set(activeJobId);
    this.branchId.set(undefined);
  }

  /**
   * Close the plan dialog
   */
  closePlanDialog(): void {
    this.isVisible.set(false);
    this.clientId.set(0);
    this.planId.set(undefined);
    this.activeJobId.set(undefined);
    this.trainingType.set(undefined);
    this.branchId.set(undefined);
  }

  /**
   * Get current state
   */
  getState(): IPlanDialogState {
    return {
      isVisible: this.isVisible(),
      clientId: this.clientId(),
      planId: this.planId(),
      activeJobId: this.activeJobId(),
      trainingType: this.trainingType(),
      branchId: this.branchId()
    };
  }

  /**
   * Notify that a plan has been saved
   */
  notifyPlanSaved(): void {
    this.planSavedSubject.next();
  }

  /**
   * Set the live workout session visibility state
   */
  setLiveWorkoutSessionVisible(isVisible: boolean): void {
    this.isLiveWorkoutSessionVisible.set(isVisible);
  }
}
